import { Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export type Mascot = {
  id: string;
  name: string;
  title: string;
  image: string;
  threshold: number;
  desc: string;
};

export const MASCOTS: Mascot[] = [
  {
    id: "ho-ly",
    name: "Hồ Ly Tinh",
    title: "Tinh khôn — khởi vận",
    image: "/mascots/ho-ly.webp",
    threshold: 1,
    desc: "Khai mở khi điểm danh ngày đầu. Mang đến trực giác nhạy bén.",
  },
  {
    id: "linh-quy",
    name: "Linh Quy",
    title: "Trường tồn — kiên định",
    image: "/mascots/linh-quy.webp",
    threshold: 3,
    desc: "Chuỗi 3 ngày. Biểu tượng của sự bền bỉ và tuổi thọ.",
  },
  {
    id: "bach-ho",
    name: "Bạch Hổ",
    title: "Tây phương trấn ải",
    image: "/mascots/bach-ho.webp",
    threshold: 7,
    desc: "Chuỗi 7 ngày. Trừ tà, hộ mệnh, mang khí phách.",
  },
  {
    id: "ky-lan",
    name: "Kỳ Lân",
    title: "Cát tường — phúc khí",
    image: "/mascots/ky-lan.webp",
    threshold: 14,
    desc: "Chuỗi 14 ngày. Báo điềm lành, vượng tài vượng lộc.",
  },
  {
    id: "phuong-hoang",
    name: "Phượng Hoàng",
    title: "Tái sinh — vinh hiển",
    image: "/mascots/phuong-hoang.webp",
    threshold: 30,
    desc: "Chuỗi 30 ngày. Lửa thiêng tái sinh, hoá giải vận xui.",
  },
  {
    id: "thanh-long",
    name: "Thanh Long",
    title: "Đông phương chí tôn",
    image: "/mascots/thanh-long.webp",
    threshold: 60,
    desc: "Chuỗi 60 ngày. Quyền uy tối thượng, thiên thời tận hưởng.",
  },
  {
    id: "than-to",
    name: "Thần Tổ Bói Đê",
    title: "Đại đạo — toàn tri",
    image: "/mascots/than-to.webp",
    threshold: 100,
    desc: "Chuỗi 100 ngày. Cảnh giới tối cao, thông tỏ thiên cơ.",
  },
];

/** Linh vật cao nhất mở khoá ứng với `score` (chuỗi/ngày bói). Null nếu chưa đạt mốc nào. */
export function mascotForScore(score: number): Mascot | null {
  let found: Mascot | null = null;
  for (const m of MASCOTS) {
    if (score >= m.threshold) found = m; // MASCOTS tăng dần theo threshold
  }
  return found;
}

export function SpiritMascotsCard({
  currentStreak,
  longestStreak,
}: {
  currentStreak: number;
  longestStreak: number;
}) {
  const unlockScore = Math.max(currentStreak, longestStreak);
  const unlockedCount = MASCOTS.filter((m) => unlockScore >= m.threshold).length;
  const nextLocked = MASCOTS.find((m) => unlockScore < m.threshold);
  const featured = [...MASCOTS].reverse().find((m) => unlockScore >= m.threshold) ?? null;

  return (
    <>
      <Card className="mb-5 overflow-hidden border-gold-soft/40 bg-card/60">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" />
            Linh vật tâm linh
            <span className="ml-auto text-xs font-normal text-muted-foreground">
              {unlockedCount}/{MASCOTS.length} đã khai mở
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Featured mascot */}
          {featured ? (
            <div className="sm-featured relative mb-5 overflow-hidden rounded-2xl border border-gold-soft/40 bg-background/40 p-5">
              <div className="pointer-events-none absolute inset-0 sm-particles">
                {Array.from({ length: 14 }).map((_, i) => (
                  <span
                    key={i}
                    className="sm-particle"
                    style={
                      {
                        left: `${(i * 53) % 100}%`,
                        animationDelay: `${(i % 7) * 0.6}s`,
                        animationDuration: `${5 + (i % 5)}s`,
                      } as React.CSSProperties
                    }
                  />
                ))}
              </div>
              <div className="relative flex flex-col items-center gap-3 text-center sm:flex-row sm:text-left">
                <div className="relative">
                  <div className="sm-halo absolute inset-0 rounded-full" />
                  <div className="sm-mascot sm-mascot-orb relative grid h-24 w-24 place-items-center rounded-full border border-gold-soft/60 bg-background/60 backdrop-blur">
                    <img
                      src={featured.image}
                      alt={featured.name}
                      className="sm-mascot-art h-[86px] w-[86px] object-contain"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-widest text-primary/80">
                    Linh vật đồng hành
                  </div>
                  <div className="text-xl font-bold text-gold-gradient">{featured.name}</div>
                  <div className="text-sm italic text-muted-foreground">{featured.title}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{featured.desc}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-5 rounded-2xl border border-dashed border-border/50 bg-background/40 p-5 text-center text-sm text-muted-foreground">
              Điểm danh ngày đầu để khai mở linh vật đầu tiên.
            </div>
          )}

          {/* Next unlock progress */}
          {nextLocked ? (
            <div className="mb-5 rounded-xl border border-border/40 bg-background/40 px-4 py-3">
              <div className="mb-2 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  Sắp khai mở:{" "}
                  <span className="font-semibold text-foreground">{nextLocked.name}</span>
                </span>
                <span className="font-mono text-primary">
                  {Math.min(unlockScore, nextLocked.threshold)}/{nextLocked.threshold}
                </span>
              </div>
              <div className="h-2 rounded-full bg-muted/40">
                <div className="relative h-full rounded-full bg-gradient-to-r from-[#F6D77A] via-[#D69A1E] to-[#A86400]" style={{ width: `${Math.min(100, (unlockScore / nextLocked.threshold) * 100)}%` }}>
                  <span className="absolute -right-1 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-[#FFE9A6] shadow-[0_0_12px_rgba(242,182,47,.95)]" />
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-5 rounded-xl border border-primary/40 bg-gold-gradient/10 px-4 py-3 text-center text-sm font-medium text-foreground">
              ✦ Bạn đã khai mở toàn bộ linh vật — Đại đạo viên mãn ✦
            </div>
          )}

          {/* Grid */}
          <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4">
            {MASCOTS.map((m) => {
              const unlocked = unlockScore >= m.threshold;
              return (
                <div
                  key={m.id}
                  className={`group relative overflow-hidden rounded-xl border p-2.5 text-center transition ${
                    unlocked
                      ? "border-gold-soft/50 bg-background/50 hover:scale-[1.03]"
                      : "border-border/40 bg-background/30"
                  }`}
                  title={m.desc}
                >
                  <div className="relative">
                    <div
                      className={`mx-auto grid h-12 w-12 place-items-center rounded-full text-2xl ${
                        unlocked
                          ? "sm-mascot-mini bg-background/70 border border-gold-soft/40"
                          : "bg-background/60 border border-border/40 opacity-35 grayscale"
                      }`}
                    >
                      <img
                        src={m.image}
                        alt={m.name}
                        className="sm-mascot-art-mini h-11 w-11 object-contain"
                      />
                    </div>
                    <div
                      className={`mt-1.5 flex min-h-[2.25rem] items-center justify-center text-[11px] font-medium leading-tight ${
                        unlocked ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {m.name}
                    </div>
                    <div className="text-[10px] text-muted-foreground">{unlocked ? `${m.threshold} ngày` : `Cần ${m.threshold} ngày`}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <style>{`
        @keyframes sm-float {
          0%, 100% { transform: translateY(0) }
          50% { transform: translateY(-6px) }
        }
        @keyframes sm-halo {
          0%, 100% { box-shadow: 0 0 30px 4px hsl(var(--primary) / 0.35), 0 0 60px 12px hsl(var(--primary) / 0.18); }
          50% { box-shadow: 0 0 45px 10px hsl(var(--primary) / 0.6), 0 0 90px 24px hsl(var(--primary) / 0.28); }
        }
        @keyframes sm-aura {
          0%, 100% { transform: scale(1); opacity: .55 }
          50% { transform: scale(1.05); opacity: .8 }
        }
        @keyframes sm-rise {
          0% { transform: translateY(110%) scale(.6); opacity: 0 }
          15% { opacity: .9 }
          100% { transform: translateY(-110%) scale(1); opacity: 0 }
        }
        .sm-mascot { animation: sm-float 4s ease-in-out infinite; }
        .sm-mascot-mini { animation: sm-float 5s ease-in-out infinite; }
        .sm-halo { animation: sm-halo 3.2s ease-in-out infinite; }
        .sm-aura { animation: sm-aura 6s ease-in-out infinite; }
        .sm-mascot-art, .sm-mascot-art-mini { transition: filter .25s ease; }
        .dark .sm-featured {
          background:
            radial-gradient(circle at 50% 24%, oklch(0.82 0.14 85 / .11), transparent 34%),
            linear-gradient(145deg, oklch(0.24 0.008 85 / .9), oklch(0.14 0.008 85 / .82));
          border-color: oklch(0.82 0.14 85 / .3);
          box-shadow: inset 0 1px 0 oklch(0.95 0.06 88 / .08);
        }
        .dark .sm-mascot-orb {
          background: radial-gradient(circle at 50% 42%, #fffdf8, #f1e7d6 74%);
          border-color: oklch(0.72 0.1 78 / .72);
          box-shadow:
            inset 0 0 18px rgb(170 116 41 / .08),
            0 10px 30px oklch(0.06 0.008 85 / .45);
          isolation: isolate;
        }
        .dark .sm-mascot-mini {
          background: radial-gradient(circle at 50% 42%, #fffdf8, #eee2cf 76%);
          border-color: oklch(0.72 0.1 78 / .58);
          box-shadow:
            inset 0 0 10px rgb(170 116 41 / .08),
            0 5px 14px oklch(0.06 0.008 85 / .4);
          isolation: isolate;
        }
        .dark .sm-mascot-art {
          filter: none;
          opacity: 1;
          mix-blend-mode: normal;
        }
        .dark .sm-mascot-mini .sm-mascot-art-mini {
          filter: none;
          opacity: 1;
          mix-blend-mode: normal;
        }
        .dark .group:hover .sm-mascot-art-mini {
          filter: drop-shadow(0 4px 7px rgb(139 91 28 / .2));
        }
        .sm-particles { overflow: hidden; }
        .sm-particle {
          position: absolute;
          bottom: -10px;
          width: 4px; height: 4px; border-radius: 9999px;
          background: radial-gradient(circle, hsl(var(--primary) / .9), hsl(var(--primary) / 0) 70%);
          filter: blur(.5px);
          animation: sm-rise linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .sm-mascot, .sm-mascot-mini, .sm-halo, .sm-aura, .sm-particle { animation: none !important; }
        }
      `}</style>
    </>
  );
}
